import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { fetchDocumentsByIds } from "../../store/actions/documentsActions";
import PreloadDocumentPages from "../preview/PreloadDocumentPages";
import { previewDocuments } from '../preview/pbreader/pdfPreviewDocuments';
import {
    createPaymentAction,
    fetchPriceApparat,
} from "../../store/actions/paymentsActions";
import { isFreePrintEnabled } from "../../services/kioskServiceSession";
import FileTypeIcon from "../../components/FileTypeIcon";
import { getDocumentFileUrl } from "../../utils/documentFiles";
import PrintSettings from "./PrintSettings";
import DocumentViewer from "../preview/DocumentViewer";
import useLivePrintModes from "./useLivePrintModes";
import PaymentModal from "./PaymentModal";
import BlankPagesModal from "./BlankPagesModal";
import useDocumentAnalysis from "../preview/useDocumentAnalysis";
import { formatPageList, selectedPageList } from "../../utils/pageRange";
import LoadingOverlay from "./LoadingOverlay";
import { usePaymentRequest } from "./usePaymentRequest";
import WarningMessage from "./WarningMessage";
import ApparatModeWarning from "./ApparatModeWarning";
import { useKioskToast, KioskToast } from "../../components/useKioskToast";
import { orderDraftRestored, orderDraftSaved } from "../../store/reducers/paymentsReducers";
import { limitMessage } from "../../utils/apparatPolicy";

const blockVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.45, delay: i * 0.1 },
    }),
};

const defaultPrintSettings = {
    copyCount: 1,
    printType: "print_all",
    customPages: "",
    orientation: "portrait",
    scaleMode: "auto",
    scalePercent: 100,
    printSide: "one_sided",
    printColor: "black_white",
};

function EditManyDocs() {
    useEffect(() => () => previewDocuments.clear(), []);
    useLivePrintModes();
    const { request, cancel: cancelPayment, showCancel: showCancelPayment } = usePaymentRequest();
    const freePrint = isFreePrintEnabled();

    const [docSettings, setDocSettings] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [showBlankModal, setShowBlankModal] = useState(false);
    // Набор пустых страниц, про который человек уже ответил «Продолжить так» —
    // при повторном «К оплате» с тем же набором не спрашиваем снова.
    const dismissedBlankKeyRef = useRef("");
    const { toastMsg, showToast } = useKioskToast();
    // null = остаток бумаги ещё не пришёл (или запрос упал) — отдельное
    // состояние от "бумаги 0", см. тот же приём в BlankSheet.jsx.
    const [availablePapers, setAvailablePapers] = useState(null);
    // null = ошибки нет; "" = ошибка без текста от сервера (переводим
    // fallback-текст при рендере, а не тут — иначе `t` тянет за собой смену
    // языка в зависимости эффекта, который её ставит).
    const [fetchError, setFetchError] = useState(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { documentData, loading, error } = useSelector(
        (state) => state.documentsList,
    );
    const { loadingPayment, priceApparat, orderDraft, restoreOrderDraft } = useSelector(
        (state) => state.payments,
    );
    const allowScaling = priceApparat?.supports_print_scaling === true && window.electronAPI?.pbreader?.supportsScale === true;
    const { t } = useTranslation();

    const idsDocs = useMemo(() => {
        const ids =
            searchParams
                .get("ids_docs")
                ?.split(",")
                .map((id) => parseInt(id.trim()))
                .filter((id) => !isNaN(id)) || [];
        return ids;
    }, [searchParams]);

    const [documents, setDocuments] = useState([]);
    const [selectedDocId, setSelectedDocId] = useState(null);
    const [docTotalPages, setDocTotalPages] = useState({});
    const [docRendered, setDocRendered] = useState({});

    const messages = [
        t("editDoc.loading_messages.processing"),
        t("editDoc.loading_messages.almost_done"),
        t("editDoc.loading_messages.waiting"),
        t("editDoc.loading_messages.finishing"),
    ];

    const [loadingMessage, setLoadingMessage] = useState(0);

    useEffect(() => {
        if (loadingPayment) {
            const interval = setInterval(() => {
                setLoadingMessage((prev) => (prev + 1) % messages.length);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [loadingPayment, messages.length]);

    useEffect(() => {
        if (idsDocs.length > 0) {
            dispatch(fetchDocumentsByIds(idsDocs)).catch((err) => {
                console.error("Failed to fetch documents:", err);
                // "" — сигнал "ошибка есть, текста нет", переводим fallback
                // при рендере (см. блок fetchError ниже).
                setFetchError(err.message || "");
            });
            dispatch(fetchPriceApparat());
        }
        // `t` НЕ в зависимостях: раньше смена языка перезапускала этот эффект,
        // а он на каждый запуск сбрасывал docLoading всех документов в true —
        // именно это гасило кнопку «К оплате» при переключении языка (см.
        // разбор в плане задачи). Готовность теперь считается не от
        // docLoading, а от docRendered/docTotalPages — см. docsReady ниже.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, idsDocs]);

    // Как и в EditDoc: режим могли выключить уже после того, как пользователь
    // выбрал цвет/дуплекс для какого-то из документов заказа — откатываем
    // только затронутые документы, не трогая настройки остальных.
    useEffect(() => {
        if (priceApparat?.is_color !== false && priceApparat?.allow_duplex !== false && priceApparat?.allow_orientation === true && allowScaling) {
            return;
        }
        setDocSettings((prev) => {
            let changed = false;
            const next = { ...prev };
            for (const docId of Object.keys(next)) {
                const settings = next[docId];
                let patched = settings;
                if (priceApparat?.is_color === false && settings.printColor === "color") {
                    patched = { ...patched, printColor: "black_white" };
                    changed = true;
                }
                if (priceApparat?.allow_duplex === false && settings.printSide === "two_sided") {
                    patched = { ...patched, printSide: "one_sided" };
                    changed = true;
                }
                next[docId] = patched;
                if (priceApparat?.allow_orientation !== true && patched.orientation !== "portrait") {
                    next[docId] = { ...next[docId], orientation: "portrait" };
                    changed = true;
                }
                if (!allowScaling && patched.scaleMode && patched.scaleMode !== "auto") {
                    next[docId] = { ...next[docId], scaleMode: "auto" };
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [priceApparat, allowScaling]);

    useEffect(() => {
        import("axios").then(({ default: axios }) => {
            const deviceToken = localStorage.getItem("deviceToken");
            axios
                .get(
                    `${process.env.REACT_APP_BASEURL}/api/v1/admin/apparats/pages`,
                    {
                        headers: { "Device-Token": `${deviceToken}` },
                    },
                )
                .then((response) =>
                    // max_tray_pages — максимум среди лотков (аппарат без
                    // заведённых лотков отдаёт его же равным count_pages),
                    // см. комментарий в EditDoc.jsx.
                    setAvailablePapers(
                        response.data.max_tray_pages ??
                            response.data.count_pages ??
                            0,
                    ),
                )
                .catch((error) => {
                    console.error("Error fetching available papers:", error);
                    // availablePapers остаётся null ("неизвестно") — не 0,
                    // иначе упавший запрос выглядел бы как пустой лоток.
                });
        });
    }, []);

    useEffect(() => {
        if (documentData && idsDocs.length > 0) {
            const filteredDocs = idsDocs
                .map((id) => documentData.find((doc) => doc?.id === id))
                .filter((doc) => doc && doc.id && doc.file_name);
            setDocuments((prevDocs) => {
                if (JSON.stringify(prevDocs) !== JSON.stringify(filteredDocs)) {
                    setDocSettings((prevSettings) => {
                        const newSettings = { ...prevSettings };
                        filteredDocs.forEach((doc) => {
                            if (!newSettings[doc.id]) {
                                newSettings[doc.id] = {
                                    ...defaultPrintSettings,
                                };
                            }
                        });
                        return newSettings;
                    });
                    return filteredDocs;
                }
                return prevDocs;
            });
            if (
                filteredDocs.length > 0 &&
                (!selectedDocId ||
                    !filteredDocs.find((doc) => doc.id === selectedDocId))
            ) {
                setSelectedDocId(filteredDocs[0].id);
            }
        }
        // `t` НЕ в зависимостях — этот эффект его и не использует; было
        // унаследовано от копии выше, только добавляло лишний перезапуск.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentData, idsDocs, selectedDocId]);

    // Предупреждение о пустых страницах включается в админке для каждого
    // аппарата: одни принтеры пустые страницы пропускают, другие печатают.
    const blankPagesWarning = priceApparat ? priceApparat.kiosk_settings?.blank_pages_warning === true : undefined;
    const analysis = useDocumentAnalysis(documents, selectedDocId, blankPagesWarning);

    // Ориентация по умолчанию — по самому документу: альбомный файл сразу
    // открывается альбомным. Только пока человек сам её не выбирал
    // (orientationChosen) и только если аппарат разрешает выбор ориентации.
    // Флаг живёт в docSettings, поэтому переживает и черновик после «Отменить».
    useEffect(() => {
        if (priceApparat?.allow_orientation !== true) return;
        setDocSettings((prev) => {
            let changed = false;
            const next = { ...prev };
            for (const [docId, result] of Object.entries(analysis)) {
                const settings = next[docId];
                if (!settings || settings.orientationChosen || !result?.landscape) continue;
                next[docId] = { ...settings, orientation: "landscape", orientationChosen: true };
                changed = true;
            }
            return changed ? next : prev;
        });
    }, [analysis, priceApparat]);

    // Пустые страницы среди тех, что уйдут в печать, — по каждому документу.
    const blankFindings = useMemo(
        () =>
            documents.flatMap((doc) => {
                const result = analysis[doc.id];
                if (!blankPagesWarning || !result?.blankPages?.length) return [];
                const settings = docSettings[doc.id] || defaultPrintSettings;
                const pages = selectedPageList(
                    settings.printType,
                    settings.customPages,
                    docTotalPages[doc.id] || result.pageCount,
                );
                const blank = new Set(result.blankPages);
                const blankPages = pages.filter((page) => blank.has(page));
                if (!blankPages.length) return [];
                return [{ doc, blankPages, remaining: pages.filter((page) => !blank.has(page)) }];
            }),
        [documents, analysis, docSettings, docTotalPages, blankPagesWarning],
    );
    const blankFindingsKey = (findings) =>
        findings.map((f) => `${f.doc.id}:${f.blankPages.join(",")}`).join(";");

    const selectedDoc = useMemo(() => {
        return documents.find((doc) => doc?.id === selectedDocId) || null;
    }, [documents, selectedDocId]);

    // Единая синхронизация вспомогательных карт с documents — раньше очистка
    // была точечной (delete по одному ключу только в handleRemoveDoc), теперь
    // работает при любом изменении списка документов, откуда бы оно ни пришло.
    const previousDocIdsRef = useRef(new Set());
    useEffect(() => {
        const currentIds = new Set(documents.map((doc) => doc.id));
        // A document that left the order must not keep its PbReader session
        // alive for the pool's idle timeout — it would still compete with
        // the remaining documents' requests to the same single-threaded
        // worker when the user proceeds to payment right after removing it.
        for (const id of previousDocIdsRef.current) {
            if (!currentIds.has(id)) previewDocuments.forget(id);
        }
        previousDocIdsRef.current = currentIds;
        const pruneStale = (setter) =>
            setter((prev) => {
                let changed = false;
                const next = {};
                for (const key of Object.keys(prev)) {
                    if (currentIds.has(Number(key))) {
                        next[key] = prev[key];
                    } else {
                        changed = true;
                    }
                }
                return changed ? next : prev;
            });
        pruneStale(setDocSettings);
        pruneStale(setDocRendered);
        pruneStale(setDocTotalPages);
    }, [documents]);

    // Возврат после «Отменить» на экране оплаты — поднимаем параметры, с
    // которыми человек ушёл платить, чтобы он поправил их, а не выбирал
    // заново. Объявлен после синхронизации documents выше намеренно: при
    // монтировании её setState (дефолты, чистка) применяются раньше этого.
    useEffect(() => {
        if (!restoreOrderDraft) return;
        dispatch(orderDraftRestored());
        if (!orderDraft || orderDraft.idsKey !== idsDocs.join(",")) return;
        setDocSettings(orderDraft.docSettings);
        setSelectedDocId(orderDraft.selectedDocId);
        // Только при монтировании: флаг одноразовый и сбрасывается здесь же.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRemoveDoc = (docId) => {
        // Последний документ не удаляем: пустой заказ не имеет смысла,
        // а молчаливый редирект на список выглядел как потеря выбора (макет: .tab-toast).
        if (documents.length <= 1) {
            showToast(t("editDoc.tab_toast_min_one"));
            return;
        }

        const updatedDocs = documents.filter((doc) => doc.id !== docId);
        setDocuments(updatedDocs);
        // docSettings/docRendered/docTotalPages подчищаются от docId
        // отдельным эффектом-синхронизацией (реагирует на смену documents),
        // а не точечными delete здесь — см. комментарий у него выше.

        if (selectedDocId === docId) {
            if (updatedDocs.length > 0) {
                setSelectedDocId(updatedDocs[0].id);
            } else {
                setSelectedDocId(null);
            }
        }

        const updatedIds = updatedDocs.map((doc) => doc.id);
        if (updatedIds.length > 0) {
            navigate(
                `/printing/edit_many_docs?ids_docs=${updatedIds.join(",")}`,
            );
        } else {
            navigate("/printing/show_list/user");
        }
    };

    const handlePayment = async (isQuickPrint = false) => {
        setShowModal(false);

        const paymentData = documents.map((doc) => {
            const totalPages = docTotalPages[doc.id] || 1;
            const settings = docSettings[doc.id] || defaultPrintSettings;
            const {
                printType,
                customPages,
                orientation,
                scaleMode = "auto",
                scalePercent = 100,
                printSide,
                printColor,
                copyCount,
            } = settings;

            let pagesToSend = "";
            if (printType === "first_page") {
                pagesToSend = "1";
            } else if (printType === "last_page") {
                pagesToSend = totalPages.toString();
            } else if (printType === "custom") {
                pagesToSend = customPages;
            }

            const pagesToPrint =
                printType === "print_all"
                    ? totalPages
                    : printType === "custom" && customPages
                      ? customPages
                            .split(",")
                            .flatMap((range) => {
                                const trimmedRange = range.trim();
                                const [start, end] = trimmedRange.includes("-")
                                    ? trimmedRange.split("-").map(Number)
                                    : [Number(trimmedRange)];
                                return end
                                    ? Array.from(
                                          { length: end - start + 1 },
                                          (_, i) => start + i,
                                      )
                                    : [Number(trimmedRange)];
                            })
                            .filter((page) => page >= 1 && page <= totalPages)
                            .length
                      : 1;

            const requiredSheets =
                printSide === "one_sided"
                    ? pagesToPrint * copyCount
                    : Math.ceil(pagesToPrint / 2) * copyCount;

            const {
                price_for_black_and_white = 30,
                price_for_color = 60,
                price_for_black_and_white_duplex = 50,
                price_for_color_duplex = 100,
                price_for_photo_bw = 50,
                price_for_photo_color = 80,
            } = priceApparat || {};

            // Фото печатается по отдельному, более высокому тарифу (краска
            // уходит как сплошная заливка); дуплекс на фото не влияет — тариф
            // всегда одностраничный, см. app/api/common/tariffs.py на бэкенде.
            const costPerSheet =
                doc.tariff === "photo"
                    ? printColor === "black_white"
                        ? price_for_photo_bw
                        : price_for_photo_color
                    : printSide === "one_sided"
                      ? printColor === "black_white"
                          ? price_for_black_and_white
                          : price_for_color
                      : printColor === "black_white"
                        ? price_for_black_and_white_duplex
                        : price_for_color_duplex;

            const amount = requiredSheets * costPerSheet;

            return {
                document_id: doc.id,
                amount,
                custom_pages: pagesToSend,
                is_album_orientation: orientation === "portrait" ? false : true,
                is_one_side: printSide === "one_sided",
                scale_mode: scaleMode,
                scale_percent: scaleMode === "custom" ? scalePercent : 100,
                is_all_pages: printType === "print_all",
                copy_count: copyCount,
                status: "pending",
                // Прямой смысл: true = ЦВЕТ (см. комментарий в EditDoc.jsx).
                is_color: printColor === "color",
                count_pages: requiredSheets,
            };
        });

        dispatch(orderDraftSaved({ idsKey: idsDocs.join(","), docSettings, selectedDocId }));

        try {
            const { cancelled, reason, resultAction } = await request(paymentData);
            if (cancelled) {
                if (reason === "timeout") showToast(t("editDoc.payment_timeout"));
                return;
            }
            if (createPaymentAction.fulfilled.match(resultAction)) {
                if (resultAction.payload?.provider === "admin_free") {
                    navigate("/service/print-sent", { replace: true });
                } else {
                    navigate(`/payments`, {
                        state: {
                            from: `/printing/edit_many_docs?ids_docs=${idsDocs.join(
                                ",",
                            )}`,
                        },
                    });
                }
            } else {
                console.error(
                    "Ошибка создания платежа:",
                    resultAction.payload || t("editDoc.payment_error"),
                );
                showToast(typeof resultAction.payload === "string" ? resultAction.payload : t("editDoc.payment_error"));
            }
        } catch (error) {
            console.error(
                "Ошибка при отправке запроса на сервер:",
                error.message,
            );
        }
    };

    const pagesToPrintPerDoc = useMemo(
        () => (docId) => {
            const totalPages = docTotalPages[docId] || 1;
            const settings = docSettings[docId] || defaultPrintSettings;
            const { printType, customPages } = settings;

            let pages = totalPages;
            if (printType === "first_page" || printType === "last_page") {
                pages = 1;
            } else if (printType === "custom" && customPages) {
                const parsedPages = customPages
                    .split(",")
                    .flatMap((range) => {
                        const trimmedRange = range.trim();
                        const [start, end] = trimmedRange.includes("-")
                            ? trimmedRange.split("-").map(Number)
                            : [Number(trimmedRange)];
                        return end
                            ? Array.from(
                                  { length: end - start + 1 },
                                  (_, i) => start + i,
                              )
                            : [Number(trimmedRange)];
                    })
                    .filter((page) => page >= 1 && page <= totalPages);
                pages = parsedPages.length;
            }
            return pages;
        },
        [docTotalPages, docSettings],
    );

    // Сколько листов уйдёт на каждый документ по отдельности — каждый документ
    // печатается своим заданием и на бэкенде уходит в СВОЙ подходящий лоток
    // (app/api/common/trays.py::plan_allocation), не обязательно в один и тот же.
    const sheetsPerDocument = useMemo(() => {
        return documents.map((doc) => {
            const pagesToPrint = pagesToPrintPerDoc(doc.id);
            const settings = docSettings[doc.id] || defaultPrintSettings;
            const { printSide, copyCount } = settings;

            const sheetsPerCopy =
                printSide === "one_sided"
                    ? pagesToPrint
                    : Math.ceil(pagesToPrint / 2);
            return sheetsPerCopy * copyCount;
        });
    }, [documents, pagesToPrintPerDoc, docSettings]);

    // Сумма — для отображения пользователю ("Итого листов"), не для гейта:
    // сравнивать её с ёмкостью одного лотка было бы неверно при нескольких
    // документах (см. maxSheetsPerDocument ниже).
    const requiredSheets = useMemo(
        () => sheetsPerDocument.reduce((total, sheets) => total + sheets, 0),
        [sheetsPerDocument],
    );

    // availablePapers — максимум в ОДНОМ лотке (см. фетч выше). Гейт сверяет
    // каждый документ по отдельности с этим максимумом, а не сумму по корзине:
    // два документа по 15 листов при лотках 20+20 должны пройти, хотя суммарно
    // 30 больше вместимости любого одного лотка (сервер раскладывает их по
    // разным лоткам). Финальное слово всё равно за сервером (create_payment).
    const maxSheetsPerDocument = useMemo(
        () => (sheetsPerDocument.length ? Math.max(...sheetsPerDocument) : 0),
        [sheetsPerDocument],
    );

    const calculateTotalCost = useMemo(() => {
        const {
            price_for_black_and_white = 30,
            price_for_color = 60,
            price_for_black_and_white_duplex = 50,
            price_for_color_duplex = 100,
            price_for_photo_bw = 50,
            price_for_photo_color = 80,
        } = priceApparat || {};

        return documents.reduce((total, doc) => {
            const pagesToPrint = pagesToPrintPerDoc(doc.id);
            const settings = docSettings[doc.id] || defaultPrintSettings;
            const { printSide, printColor, copyCount } = settings;

            // Фото — отдельный, более высокий тариф; дуплекс на него не влияет,
            // см. app/api/common/tariffs.py на бэкенде.
            const costPerSheet =
                doc.tariff === "photo"
                    ? printColor === "black_white"
                        ? price_for_photo_bw
                        : price_for_photo_color
                    : printSide === "one_sided"
                      ? printColor === "black_white"
                          ? price_for_black_and_white
                          : price_for_color
                      : printColor === "black_white"
                        ? price_for_black_and_white_duplex
                        : price_for_color_duplex;

            const sheetsPerCopy =
                printSide === "one_sided"
                    ? pagesToPrint
                    : Math.ceil(pagesToPrint / 2);

            return total + sheetsPerCopy * copyCount * costPerSheet;
        }, 0);
    }, [documents, pagesToPrintPerDoc, docSettings, priceApparat]);

    // Готовность — из АКТУАЛЬНОГО списка documents, а не из отдельной карты
    // docLoading. Object.values(docLoading).every(...) читал устаревшие ключи
    // (документы, которых уже нет в заказе) и давал true на пустом объекте —
    // именно это позволяло смене языка/удалению документа гасить кнопку
    // «К оплате» навсегда, ничего в неё не возвращая.
    const docsReady =
        documents.length > 0 &&
        documents.every(
            (doc) => docRendered[doc.id] && (docTotalPages[doc.id] ?? 0) > 0,
        );
    const papersKnown = availablePapers !== null;
    const ordersPaused = priceApparat?.accept_orders === false || (!freePrint && priceApparat?.paid_orders_available === false);
    const photoBlocked =
        documents.some((doc) => doc.tariff === "photo") &&
        priceApparat?.allow_photo_print === false;
    // Лимиты заказа из панели; бесплатная сервисная печать ими не ограничена
    // — так же, как на сервере.
    const policyMessage = freePrint
        ? null
        : limitMessage(
              priceApparat,
              {
                  sheets: requiredSheets,
                  copies: Math.max(0, ...documents.map((doc) => (docSettings[doc.id] || defaultPrintSettings).copyCount)),
                  documents: documents.length,
              },
              t,
          );
    const canPrint =
        docsReady &&
        // Пока остаток бумаги неизвестен (запрос ещё летит или упал) — не
        // блокируем оплату лимитом, последнее слово всё равно за сервером
        // в create_payment. Блокируем только когда точно знаем, что не хватит.
        (!papersKnown || availablePapers >= maxSheetsPerDocument) &&
        !ordersPaused &&
        !photoBlocked &&
        !policyMessage;
    // И "бумаги 0", и "бумаги меньше, чем нужно" — оба варианта недостачи,
    // должны показывать плашку (раньше "0" не считался, см. BlankSheet.jsx).
    const showWarning =
        papersKnown &&
        (availablePapers <= 0 || availablePapers < maxSheetsPerDocument);

    // «К оплате»: сперва предупреждение о пустых страницах (если есть и
    // ещё не отвечено), потом обычное подтверждение заказа.
    const openConfirmation = () => {
        if (blankFindings.length && blankFindingsKey(blankFindings) !== dismissedBlankKeyRef.current) {
            setShowBlankModal(true);
        } else {
            setShowModal(true);
        }
    };

    const handleKeepBlankPages = () => {
        dismissedBlankKeyRef.current = blankFindingsKey(blankFindings);
        setShowBlankModal(false);
        setShowModal(true);
    };

    // Убираем пустые страницы через обычный «свой диапазон» — дальше цена,
    // превью и платёж работают с ним как с введённым вручную.
    const handleRemoveBlankPages = () => {
        const removable = blankFindings.filter((f) => f.remaining.length > 0);
        setDocSettings((prev) => {
            const next = { ...prev };
            for (const { doc, remaining } of removable) {
                next[doc.id] = { ...next[doc.id], printType: "custom", customPages: formatPageList(remaining) };
            }
            return next;
        });
        // Документы, где пустые все выбранные страницы, остаются как были —
        // про них уже предупредили.
        dismissedBlankKeyRef.current = blankFindingsKey(blankFindings.filter((f) => !f.remaining.length));
        setShowBlankModal(false);
        setShowModal(true);
    };

    const handleDocLoadSuccess = (docId, pages) => {
        setDocTotalPages((prev) => ({ ...prev, [docId]: pages }));
        setDocRendered((prev) => ({ ...prev, [docId]: true }));
    };

    if (fetchError !== null || error) {
        return (
            <div className="h-[88vh] px-[2.4vw] py-[2vh] flex items-center justify-center bg-[#EDEFF4]">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="text-center text-[1.8vh] font-bold text-[#C2543E]"
                >
                    {t("editDoc.error")}:{" "}
                    {fetchError !== null
                        ? fetchError || t("editDoc.fetch_error")
                        : typeof error === "object"
                          ? error.detail || t("editDoc.error_default")
                          : error}
                </motion.div>
            </div>
        );
    }

    if (documents.length === 0 && !loading) {
        return (
            <div className="h-[88vh] px-[2.4vw] py-[2vh] flex items-center justify-center bg-[#EDEFF4]">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="text-center text-[1.8vh] font-bold text-[#7B8290]"
                >
                    {t("editDoc.no_document")}
                </motion.div>
            </div>
        );
    }

    const selected = docSettings[selectedDocId] || defaultPrintSettings;

    // Настройки хранятся отдельно по каждому docId, поэтому один сеттер на все
    // параметры выбранного документа: переключение таба ничего не сбрасывает.
    const updateSettings = (patch) =>
        setDocSettings((prev) => ({
            ...prev,
            [selectedDocId]: { ...prev[selectedDocId], ...patch },
        }));
    const updateSetting = (key) => (value) => updateSettings({ [key]: value });

    const selectedPages = selectedDocId ? pagesToPrintPerDoc(selectedDocId) : 0;

    return (
        <>
            <LoadingOverlay
                loading={loading}
                loadingPayment={loadingPayment}
                messages={messages}
                loadingMessage={loadingMessage}
                t={t}
                showCancelPayment={showCancelPayment}
                onCancelPayment={cancelPayment}
            />
            <AnimatePresence>
                {showBlankModal && (
                    <BlankPagesModal
                        findings={blankFindings}
                        onRemove={handleRemoveBlankPages}
                        onKeep={handleKeepBlankPages}
                        t={t}
                    />
                )}
            </AnimatePresence>
            <AnimatePresence>
                {showModal && (
                    <PaymentModal
                        showModal={showModal}
                        setShowModal={setShowModal}
                        handlePayment={handlePayment}
                        documentName={selectedDoc?.file_name}
                        documentsCount={documents.length}
                        printType={selected.printType}
                        customPages={selected.customPages}
                        printColor={selected.printColor}
                        orientation={selected.orientation}
                        printSide={selected.printSide}
                        copyCount={selected.copyCount}
                        pagesToPrint={selectedPages}
                        calculateTotalCost={calculateTotalCost}
                        t={t}
                        scaleMode={allowScaling ? selected.scaleMode : undefined}
                        scalePercent={selected.scalePercent}
                    />
                )}
            </AnimatePresence>
            <div className="h-[88vh] px-[2.4vw] py-[2vh] flex flex-col gap-[1.2vh] bg-[#EDEFF4]">
                {/* Табы документов заказа */}
                <motion.div
                    variants={blockVariants}
                    initial="hidden"
                    animate="visible"
                    custom={0}
                    // Строго одна строка: при flex-wrap три длинных имени
                    // переносились на второй ряд и сдвигали вниз всю страницу
                    // (превью, настройки и панель оплаты), а высоты там и так
                    // впритык. Табы делят ширину поровну, имя обрезается.
                    className="flex-none relative flex gap-[0.8vw]"
                >
                    <KioskToast message={toastMsg} position="top" />

                    {documents.map((doc) => {
                        const isSelected = selectedDocId === doc.id;
                        const extension = doc.file_name
                            .split(".")
                            .pop()
                            .toLowerCase();

                        return (
                            <div
                                key={doc.id}
                                onClick={() => setSelectedDocId(doc.id)}
                                title={doc.file_name}
                                className={`flex-1 min-w-0 max-w-[34vw] bg-white rounded-full pl-[1.2vw] pr-[0.5vw] py-[0.7vh] flex items-center gap-[0.7vw] text-[1.35vh] font-bold transition-colors ${
                                    isSelected
                                        ? "text-[#12161F] outline outline-[0.25vh] outline-[#246CE2]"
                                        : "text-[#7B8290] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)]"
                                }`}
                            >
                                <FileTypeIcon
                                    extension={extension}
                                    className="flex-none w-[2.1vh] h-[2.5vh]"
                                />
                                <span className="truncate min-w-0">
                                    {doc.file_name || `Doc ${doc.id}`}
                                </span>
                                <button data-analytics="editmanydocs.action.1"
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveDoc(doc.id);
                                    }}
                                    className={`flex-none w-[3vh] h-[3vh] rounded-full flex items-center justify-center transition-colors ${
                                        isSelected
                                            ? "bg-[#E3ECFB] text-[#246CE2]"
                                            : "bg-[#F1F3F7] text-[#7B8290]"
                                    } active:bg-[#C2543E] active:text-white`}
                                >
                                    <svg
                                        className="w-[1.4vh] h-[1.4vh]"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.6"
                                        strokeLinecap="round"
                                    >
                                        <path d="M6 6l12 12M18 6L6 18" />
                                    </svg>
                                </button>
                            </div>
                        );
                    })}
                </motion.div>

                {/* Предпросмотр + параметры печати выбранного документа */}
                <div className="flex-1 min-h-0 flex gap-[1.2vw]">
                    <motion.div
                        variants={blockVariants}
                        initial="hidden"
                        animate="visible"
                        custom={1}
                        className="flex-[1.3] min-w-0 min-h-0 bg-white rounded-[3vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] px-[1vw] py-[1.2vh] flex flex-col items-center gap-[0.8vh]"
                    >
                        <span className="flex-none text-[1.1vh] font-bold uppercase tracking-[0.15em] text-[#7B8290]">
                            {t("editDoc.preview_label")}
                        </span>
                        {!selectedDoc ? (
                            <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-[1.2vh]">
                                <svg
                                    className="w-[3.6vh] h-[3.6vh]"
                                    style={{
                                        animation: "spin 0.8s linear infinite",
                                    }}
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#246CE2"
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                >
                                    <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
                                </svg>
                                <p className="text-[1.6vh] font-semibold text-[#7B8290]">
                                    {t("editManyDocs.loading")}
                                </p>
                            </div>
                        ) : (
                            // Keep a prefixed key: a sibling keyed by the same bare id once made
                            // React lose the old viewer and leave it mounted on every switch.
                            <DocumentViewer
                                key={`viewer-${selectedDocId}`}
                                currentDocument={selectedDoc}
                                printType={selected.printType}
                                customPages={selected.customPages}
                                printColor={selected.printColor}
                                orientation={selected.orientation}
                                printSide={selected.printSide}
                                copyCount={selected.copyCount}
                                onLoadSuccess={(pages) =>
                                    handleDocLoadSuccess(selectedDocId, pages)
                                }
                                scaleMode={selected.scaleMode}
                                scalePercent={selected.scalePercent}
                            />
                        )}
                    </motion.div>

                    <motion.div
                        variants={blockVariants}
                        initial="hidden"
                        animate="visible"
                        custom={2}
                        className="flex-1 min-w-0 min-h-0 flex"
                    >
                        <PrintSettings
                            printType={selected.printType}
                            setPrintType={updateSetting("printType")}
                            customPages={selected.customPages}
                            setCustomPages={updateSetting("customPages")}
                            orientation={selected.orientation}
                            setOrientation={(value) => updateSettings({ orientation: value, orientationChosen: true })}
                            printSide={selected.printSide}
                            setPrintSide={updateSetting("printSide")}
                            printColor={selected.printColor}
                            setPrintColor={updateSetting("printColor")}
                            copyCount={selected.copyCount}
                            setCopyCount={updateSetting("copyCount")}
                            totalPages={docTotalPages[selectedDocId]}
                            allowColor={priceApparat?.is_color !== false}
                            allowDuplex={priceApparat?.allow_duplex !== false}
                            allowOrientation={priceApparat?.allow_orientation === true}
                            allowScaling={allowScaling}
                            scaleMode={selected.scaleMode}
                            scalePercent={selected.scalePercent}
                            setScaleMode={updateSetting("scaleMode")}
                            setScalePercent={updateSetting("scalePercent")}
                        />
                    </motion.div>
                </div>

                {/* Скрытая предзагрузка: узнаём число страниц у невыбранных документов */}
                <div style={{ display: "none" }}>
                    {documents.map((doc) => {
                        if (docTotalPages[doc.id]) return null;
                        const fileUrl = window.electronAPI?.pbreader
                            ? `${process.env.REACT_APP_BASEURL}/api/v1/documents/${doc.id}/print-source`
                            : getDocumentFileUrl(doc);
                        return (
                            <PreloadDocumentPages
                                key={`preload-${doc.id}`}
                                documentId={doc.id}
                                fileUrl={fileUrl}
                                onLoadSuccess={(pages) =>
                                    handleDocLoadSuccess(doc.id, pages)
                                }
                            />
                        );
                    })}
                </div>

                <WarningMessage
                    showWarning={showWarning}
                    availablePapers={availablePapers}
                    requiredSheets={maxSheetsPerDocument}
                    t={t}
                />
                <ApparatModeWarning
                    ordersPaused={ordersPaused}
                    photoBlocked={photoBlocked}
                    message={policyMessage}
                    t={t}
                />

                {/* Итог и переход к подтверждению */}
                <motion.div
                    variants={blockVariants}
                    initial="hidden"
                    animate="visible"
                    custom={3}
                    className="flex-none bg-white rounded-[3vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] py-[1.4vh] px-[2.4vw] flex items-center gap-[2vw]"
                >
                    <div className="min-w-0">
                        <span className="text-[1.1vh] font-bold uppercase tracking-[0.15em] text-[#7B8290]">
                            {t("editDoc.total")}
                        </span>
                        <div className="text-[4.2vh] font-extrabold tracking-tight text-[#246CE2] leading-none mt-[0.4vh]">
                            {freePrint ? 0 : calculateTotalCost} ₸
                        </div>
                        {/* Сумма считается по всему заказу, поэтому при нескольких
                            документах расшифровка тоже про весь заказ, а не про выбранный */}
                        <div className="text-[1.25vh] font-semibold text-[#7B8290] mt-[0.5vh] truncate">
                            {documents.length > 1
                                ? t("editDoc.detail_multi", {
                                      docs: documents.length,
                                      sheets: requiredSheets,
                                  })
                                : t("editDoc.detail", {
                                      pages: selectedPages,
                                      copies: selected.copyCount,
                                      color: t(
                                          `editDoc.print_colors.${selected.printColor}`,
                                      ),
                                  })}
                        </div>
                    </div>
                    <button data-analytics="editmanydocs.editDoc.free_print.2"
                        type="button"
                        onClick={openConfirmation}
                        disabled={!canPrint}
                        className="ml-auto flex-none flex items-center gap-[0.8vw] bg-[#246CE2] text-white rounded-full py-[1.4vh] px-[2.4vw] text-[1.8vh] font-bold transition-colors disabled:opacity-40 active:bg-[#1B55B4]"
                    >
                        {freePrint ? t("editDoc.free_print") : t("editDoc.to_payment")}
                        <svg
                            className="w-[2.1vh] h-[2.1vh]"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M5 12h14M13 6l6 6-6 6" />
                        </svg>
                    </button>
                </motion.div>
            </div>
        </>
    );
}

export default EditManyDocs;
